import type { CatalogListItem } from "./catalog-item";
import { deleteBlock } from "./catalog-item-detail";

// The catalog list's per-item actions menu: why each of its three verb rows is
// refused, when it is.
//
// Pure, and separate from the component, because the states multiply — approved
// x core x role x usage — and because two of the four are owner-ruled parity
// breaks whose *precedence* is itself a ruling (#450 R10). A rule that lives in
// JSX is a rule asserted sixteen times through a rendered menu.
//
// **Edit item is not here.** It is a link rather than a verb, and nothing
// refuses it: the donor's list row draws it unconditionally
// (`item-actions-button.tsx:126-130`). For a Division Editor, Edit item and
// Delete item are drawn or left out together on the item's launched read
// (#669 D6), and that is the menu's own gate (`item-actions-menu.tsx`) — a row
// that is absent, not a refusal with a reason to give.

/**
 * Python's core-status refusal, verbatim — `_CORE_STATUS_REFUSED_MESSAGE`
 * (`server/app/use_cases/catalog/write_access.py`).
 *
 * **One constant, deliberately shared.** Owner ruling #450 R9: the tooltip that
 * explains the disabled row and the message the wire sends back on a refusal are
 * the same sentence, so a user who gets past the affordance hears exactly what
 * the affordance said. Two copies of it is two things to keep in step, and the
 * one that drifts is the one nobody reads.
 * `catalog-item-rules-drift.unit.test.ts` pins it to the Python source.
 */
export const CORE_STATUS_ROLE_TOOLTIP =
  "Core status and EI tiers can only be changed by an administrator or a national editor.";

/**
 * Why one menu row is refused — or that it is not.
 *
 * Four states rather than a nullable reason string, because "refused and saying
 * nothing" is a real state the donor has and it is *not* the same as enabled:
 *
 *   - `enabled` — offer it.
 *   - `silent` — disabled, with no explanation. The donor's own two core-row
 *     gates (`item-actions-button.tsx:117, 120`), which carry no tooltip.
 *   - `role` — disabled because this user's role may not set core status.
 *   - `usage` — disabled because specifications still use the item.
 *
 * `role` and `usage` are distinguished even though both carry a reason: they are
 * separate owner rulings (R3 and R2), and collapsing them would make the
 * precedence rule below untestable.
 */
export type CatalogItemMenuGate =
  | { kind: "enabled" }
  | { kind: "silent" }
  | { kind: "role"; reason: string }
  | { kind: "usage"; reason: string };

/** The three verb rows' gates. Edit item is ungated, so it is absent. */
export interface CatalogListItemMenuGates {
  markCore: CatalogItemMenuGate;
  removeCore: CatalogItemMenuGate;
  deleteItem: CatalogItemMenuGate;
}

const ENABLED: CatalogItemMenuGate = { kind: "enabled" };
const SILENT: CatalogItemMenuGate = { kind: "silent" };

/**
 * Every row's gate for one list item.
 *
 * @param item The card's or row's **own** item, never the variant being
 *   previewed — the donor is explicit about it (`item-card.tsx:52-56`), and it
 *   is the opposite of the details page.
 * @param canSetCoreStatus Whether this user may set core status at all, which is
 *   a property of the session rather than of the item and so arrives from the
 *   page rather than being read off the row.
 */
export function catalogItemMenuGates(
  item: CatalogListItem,
  canSetCoreStatus: boolean,
): CatalogListItemMenuGates {
  // **The role reason outranks the silent ones** (#450 R10). An unapproved item
  // fails both conditions, and the donor's silent refusal would say the item is
  // the problem when the user's role is — the one thing they cannot fix by
  // approving it.
  const coreGate = (silent: boolean): CatalogItemMenuGate => {
    if (!canSetCoreStatus) return { kind: "role", reason: CORE_STATUS_ROLE_TOOLTIP };
    return silent ? SILENT : ENABLED;
  };

  const usageReason = deleteBlock(item.usageCount);

  return {
    markCore: coreGate(!item.isApproved || item.isCore),
    removeCore: coreGate(!item.isApproved || !item.isCore),
    deleteItem: usageReason ? { kind: "usage", reason: usageReason } : ENABLED,
  };
}
